import {
  addMonths,
  addWeeks,
  addYears,
  endOfDay,
  endOfMonth,
  isAfter,
  isBefore,
  parseISO,
  startOfWeek,
  startOfDay,
  startOfMonth,
  subDays,
} from "date-fns";
import { useMemo } from "react";
import useUser from "@/hooks/useUser";
import {
  useGetVenuePortalUserDashboardMetricsQuery,
  VenuePortalUserDashboardMetric,
} from "@/graphql/operations";
import {
  DASHBOARD_LOOKBACK_MONTHS,
  GRAPH_SCOPE_BUCKET_COUNT,
  GRAPH_SCOPE_ROLLING_WINDOW_DAYS,
} from "../constants";
import {
  DashboardChartMetrics,
  DashboardGraphScope,
  DashboardMetricTotals,
} from "../types";
import { createEmptyTotals } from "../utils";

type DashboardMetricRow = DashboardMetricTotals & {
  date: string;
  productId: string;
  venueId: string | null;
};

const addMetricToTotals = (
  totals: DashboardMetricTotals,
  metric: DashboardMetricTotals,
) => {
  totals.accepted += metric.accepted;
  totals.cancellationNoShowRevenue += metric.cancellationNoShowRevenue;
  totals.guests += metric.guests;
  totals.pending += metric.pending;
  totals.rejected += metric.rejected;
  totals.reservations += metric.reservations;
  totals.revenue += metric.revenue;
  totals.unanswered += metric.unanswered;
};

const flattenDailyMetrics = (dailyMetrics: VenuePortalUserDashboardMetric[]) =>
  dailyMetrics.flatMap((day) =>
    day.venues.flatMap((venue) =>
      venue.products.map((product) => ({
        ...product,
        date: day.date,
        venueId: venue.venueId ?? null,
      })),
    ),
  );

const filterMetrics = ({
  metrics,
  productId,
  venueId,
}: {
  metrics: DashboardMetricRow[];
  productId?: string;
  venueId?: string;
}) =>
  metrics.filter((metric) => {
    if (venueId && metric.venueId !== venueId) return false;
    if (productId && metric.productId !== productId) return false;

    return true;
  });

const GRAPH_SCOPE_FNS: Record<
  DashboardGraphScope,
  { bucketStart: (date: Date) => Date; addBucket: (date: Date, n: number) => Date }
> = {
  month: { bucketStart: startOfMonth,  addBucket: addMonths },
  week:  { bucketStart: startOfWeek,   addBucket: addWeeks },
  year:  { bucketStart: (date) => new Date(date.getFullYear(), 0, 1), addBucket: addYears },
};

const getBucketStart = (date: Date, graphScope: DashboardGraphScope) =>
  GRAPH_SCOPE_FNS[graphScope].bucketStart(date);

const addBucket = (date: Date, n: number, graphScope: DashboardGraphScope) =>
  GRAPH_SCOPE_FNS[graphScope].addBucket(date, n);

const getChartRangeStart = (today: Date, graphScope: DashboardGraphScope) => {
  const bucketCount = GRAPH_SCOPE_BUCKET_COUNT[graphScope];
  return addBucket(getBucketStart(today, graphScope), -bucketCount + 1, graphScope);
};

const createChartBuckets = (
  today: Date,
  graphScope: DashboardGraphScope,
): DashboardChartMetrics[] => {
  const rangeStart = getChartRangeStart(today, graphScope);
  const bucketCount = GRAPH_SCOPE_BUCKET_COUNT[graphScope];
  const buckets: DashboardChartMetrics[] = [];
  let bucketStart = rangeStart;

  for (let index = 0; index < bucketCount; index += 1) {
    buckets.push({
      bucketStart,
      ...createEmptyTotals(),
    });
    bucketStart = addBucket(bucketStart, 1, graphScope);
  }

  return buckets;
};

const buildChartMetrics = ({
  graphScope,
  rows,
  today,
}: {
  graphScope: DashboardGraphScope;
  rows: DashboardMetricRow[];
  today: Date;
}) => {
  const buckets = createChartBuckets(today, graphScope);
  const bucketMap = new Map(
    buckets.map((bucket) => [bucket.bucketStart.getTime(), bucket]),
  );

  rows.forEach((row) => {
    const bucket = bucketMap.get(
      getBucketStart(parseISO(row.date), graphScope).getTime(),
    );

    if (!bucket) return;

    addMetricToTotals(bucket, row);
  });

  return buckets;
};

const buildRollingMetrics = ({
  rows,
  current,
  previous,
}: {
  rows: DashboardMetricRow[];
  current: { from: Date; until: Date };
  previous: { from: Date; until: Date };
}) =>
  rows.reduce(
    (acc, row) => {
      const metricDate = parseISO(row.date);

      if (!isBefore(metricDate, current.from) && !isAfter(metricDate, current.until)) {
        addMetricToTotals(acc.current, row);
      } else if (!isBefore(metricDate, previous.from) && !isAfter(metricDate, previous.until)) {
        addMetricToTotals(acc.previous, row);
      }

      return acc;
    },
    { current: createEmptyTotals(), previous: createEmptyTotals() },
  );

export default function useDashboardMetrics({
  graphScope,
  productId,
  venueId,
}: {
  graphScope: DashboardGraphScope;
  productId?: string;
  venueId?: string;
}) {
  const {
    user: { userId },
  } = useUser();
  const today = useMemo(() => new Date(), []);
  const rollingWindowDays = GRAPH_SCOPE_ROLLING_WINDOW_DAYS[graphScope];
  const rollingWindow = useMemo(
    () => ({
      current: {
        from: startOfDay(subDays(today, rollingWindowDays - 1)),
        until: endOfDay(today),
      },
      previous: {
        from: startOfDay(subDays(today, rollingWindowDays * 2 - 1)),
        until: endOfDay(subDays(today, rollingWindowDays)),
      },
    }),
    [rollingWindowDays, today],
  );
  const rangeStart = useMemo(
    () => startOfMonth(addMonths(today, -(DASHBOARD_LOOKBACK_MONTHS - 1))),
    [today],
  );
  const rangeEnd = useMemo(() => endOfMonth(today), [today]);
  const variables = useMemo(
    () => ({
      venuePortalUserId: userId ?? "",
      currentDate: startOfDay(today),
      range: {
        from: rangeStart,
        until: rangeEnd,
      },
    }),
    [rangeEnd, rangeStart, today, userId],
  );
  const { data, error, loading } = useGetVenuePortalUserDashboardMetricsQuery({
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip: !userId,
    variables,
  });
  const rawMetrics =
    userId && data?.venuePortalUser?.id === userId
      ? data.venuePortalUser.dashboardMetrics
      : undefined;
  const filteredRows = useMemo(
    () =>
      filterMetrics({
        metrics: flattenDailyMetrics(rawMetrics ?? []),
        productId,
        venueId,
      }),
    [productId, rawMetrics, venueId],
  );
  const metrics = useMemo(
    () =>
      buildChartMetrics({
        graphScope,
        rows: filteredRows,
        today,
      }),
    [filteredRows, graphScope, today],
  );
  const rollingMetrics = useMemo(
    () =>
      buildRollingMetrics({
        rows: filteredRows,
        current: rollingWindow.current,
        previous: rollingWindow.previous,
      }),
    [filteredRows, rollingWindow],
  );

  return {
    error,
    hasData: metrics.some((metric) => metric.reservations > 0),
    loading,
    metrics,
    rollingWindowDays,
    rollingMetrics,
  };
}
